/// <amd-module name="VSSUI/Components/IdentityCard/IdentityCardContent" />
import * as React from "react";
import * as Resources from "VSSUI/Resources.Persona";

import { Callout } from "VSSUI/Callout";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { IOrigin, Location } from "VSSUI/Utilities/Position";
import { IIdentity } from "../Persona/Persona.Props";

import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css, KeyCode } from "VSSUI/Util";
import { ContactCard } from "./ContactCard";
import { DefaultAbridgedCard } from "./DefaultAbridgedCard";
import { DefaultCard } from "./DefaultCard";
import { DefaultSimpleCard } from "./DefaultSimpleCard";
import { GitHubCard } from "./GitHubCard";
import { GroupCard } from "./GroupCard";
import { GroupMembersCard } from "./GroupMembersCard";
import { CardType, IdentityCardContentProps } from "./IdentityCard.Props";
import { IdentityCardHeaderElement as HeaderElement } from "./IdentityCardHeaderElement";
import * as Utils from "./IdentityCardIdentityUtils";
import { OrganizationCard } from "./OrganizationCard";
import { ServicePrincipalCard } from "./ServicePrincipalCard";

/**
 * The content of the callout for the persona card.
 */
export class IdentityCardContent extends React.Component<IdentityCardContentProps> {
    private calloutAnchorOrigin: IOrigin = { horizontal: Location.start, vertical: Location.start };

    public componentDidMount(): void {
        window.addEventListener('resize', this.determineAnchorOrigin);
        this.determineAnchorOrigin();
    }

    public componentWillUnmount(): void {
        window.removeEventListener('resize', this.determineAnchorOrigin);    
    }

    // Render
    public render(): JSX.Element {
        // Get target element
        const targetElement: HTMLElement | undefined = this.props.referenceHTMLComponent ? this.props.referenceHTMLComponent : this.props.target;
        // Use component state information to render component
        return (
            <Callout
                onDismiss={this.onDismissCallout}
                anchorElement={targetElement}
                anchorOrigin={this.calloutAnchorOrigin}
                calloutOrigin={{ horizontal: Location.start, vertical: Location.start }}
                ariaLabel={Resources.IdentityCardProfileCardAriaLabel}
                className="bolt-identity-card-callout depth-8"
                contentClassName="v-scroll-auto"
                escDismiss={true}
                lightDismiss={true}
                ref={this.props.calloutRef}
            >
                <div>{this.renderCard()}</div>
            </Callout>
        );
    }

    private determineAnchorOrigin = () => {
        this.calloutAnchorOrigin = window.matchMedia('(max-width: 600px)').matches ? { horizontal: Location.start, vertical: Location.start } : { horizontal: Location.end, vertical: Location.start };
    }

    private onKeyDown = (ev: React.KeyboardEvent<HTMLDivElement>): void => {
        if (ev.which === KeyCode.escape) {
            this.onDismissCallout();
            ev.preventDefault();
        }
    };

    private renderCard(): JSX.Element {
        const cssClassName = "bolt-identity-card scroll-h-hidden";
        if (this.props.working && !Utils.isCompleteIdentity(this.props.dataProps.identity)) {
            // Still working to get initial data
            return (
                <div className={css(cssClassName, "loading flex-row justify-center")}>
                    <Spinner label={Resources.Loading} size={SpinnerSize.large} className="bolt-identity-card-loading-spinner" />
                </div>
            );
        }

        const isAdOrAadOrGroup =
            Utils.isAdUser(this.props.dataProps.identity) ||
            Utils.isAadUser(this.props.dataProps.identity) ||
            Utils.isGroup(this.props.dataProps.identity);
        const headerIdentity: IIdentity | undefined = this.props.dataProps.header || undefined;
        const innerCard = this.renderInnerCard();

        return innerCard ? (
            <FocusZone
                circularNavigation={true}
                defaultActiveElement=".bolt-identity-card-focus-element"
                direction={FocusZoneDirection.Vertical}
                focusOnMount={true}
                handleTabKey={true}
            >
                <div className="bolt-identity-card-focus-element" tabIndex={-1} />
                <div className={cssClassName} onKeyDown={this.onKeyDown}>
                    {isAdOrAadOrGroup && headerIdentity && <HeaderElement identity={headerIdentity} onClickFunction={this.props.onHeaderClick} />}
                    {innerCard}
                </div>
            </FocusZone>
        ) : (
            <div />
        );
    }

    private renderInnerCard(): JSX.Element {
        // Get component state data
        const { identity, successors, managerList, directReportList } = this.props.dataProps;

        if (!identity || this.props.showUnknownUser === true) {
            return (
                <div className="bolt-identity-card-content bolt-identity-card-unknown-user-content flex-column flex-center justify-center title-xs scroll-hidden">
                    {Resources.IdentityCardUnknownUser}
                </div>
            );
        }

        const isGroup = Utils.isGroup(identity);
        const isVsdUser = Utils.isVsdUser(identity);
        const isWmdUser = Utils.isWmdUser(identity);
        const isAadUser = Utils.isAadUser(identity);
        const isAadSp = Utils.isAadServicePrincipal(identity);
        const isAdUser = Utils.isAdUser(identity);
        const isGithubUser = Utils.isGithubUser(identity);

        if (isGroup) {
            switch (this.props.dataProps.cardType) {
                case CardType.Organization:
                    return <GroupMembersCard identity={identity} members={successors} onClickEntity={this.props.onClickEntity} />;
                case CardType.Default:
                    return (
                        <GroupCard
                            identity={identity}
                            isPreviousHeader={!!this.props.dataProps.previousDataState || !!this.props.initialHeader}
                            members={successors}
                            showOrganizationCard={this.props.onShowOrganizationCard}
                        />
                    );
            }
        } else if (isGithubUser) {
            return <GitHubCard identity={identity} />;
        } else if (isVsdUser) {
            return <DefaultAbridgedCard identity={identity} />;
        } else if (isWmdUser) {
            return <DefaultSimpleCard identity={identity} />;
        } else if (isAadSp) {
            return <ServicePrincipalCard identity={identity} />;
        } else if (isAadUser || isAdUser) {
            switch (this.props.dataProps.cardType) {
                case CardType.Contact:
                    return <ContactCard identity={identity} />;
                case CardType.Organization:
                    return (
                        <OrganizationCard
                            identity={identity}
                            managerList={managerList}
                            directReportList={directReportList}
                            onClickEntity={this.props.onClickEntity}
                        />
                    );
                case CardType.Default:
                default:
                    const manager = managerList && managerList.length > 0 ? managerList[managerList.length - 1] : undefined;
                    return (
                        <DefaultCard
                            identity={identity}
                            manager={manager}
                            isPreviousHeader={!!this.props.dataProps.previousDataState || !!this.props.initialHeader}
                            showContactCard={this.props.onShowContactCard}
                            showOrganizationCard={this.props.onShowOrganizationCard}
                            onClickEntity={this.props.onClickEntity}
                        />
                    );
            }
        }

        return <div />;
    }

    // State change helper methods
    private onDismissCallout = (): void => {
        if (this.props.onDismissCallback) {
            this.props.onDismissCallback();
        }
    };
}
